/* eslint-disable @typescript-eslint/no-use-before-define */

import { useVoiceRecognize } from '@euler/app/components/voice/useVoiceRecognize';
import { Center } from '@euler/components/Center';
import { MaybeText } from '@euler/components/MaybeText';
import { Spinner } from '@euler/components/Spinner';
import { FormElementClear } from '@euler/components/form/elements-ui/Clear';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ValidateError, ValidateErrorObject } from '@euler/lib/form/controller';
import { VoiceRecognizeOptions } from '@euler/services/voice.service';
import { makeDebug } from '@euler/utils';
import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { Mic } from 'lucide-react-native';
import { AnimatePresence, MotiView, motify } from 'moti';
import { identity, juxt, partial } from 'ramda';
import {
  FC,
  ReactNode,
  RefObject,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  DimensionValue,
  LayoutChangeEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputProps,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';

type ErrorMsg = ValidateError | ValidateErrorObject | null;

const debug = makeDebug('form-input', false);

const MotiTextInput = motify(TextInput)();
const MotiBottomSheetTextInput = motify(BottomSheetTextInput)();

type ErrorDisplayStyle = 'default' | 'tip';

const kDefaultClearWidth = 32;
const kDefaultClearButtonSize = 7.5;

export const useBaseInputStyle = () => {
  const { rpx } = useResponsiveContext();
  const config = useContext(InputContext);
  const { input } = useTheme().form;
  return {
    padding: config?.padding ?? rpx(10),
    borderRadius: config?.borderRadius ?? rpx(8),
    borderWidth: config?.borderWidth ?? rpx(2),
    fontSize: config?.fontSize ?? rpx(16),
    contentHeight: config?.contentHeight ?? rpx(22),
    errorBottomOffset: rpx(-6),
    inputOffsetOnError: rpx(-8),
    borderColor: config?.borderColor ?? input.borderColor,
    borderColorError: config?.borderColorError ?? input.borderColorError,
    borderColorFocused: config?.borderColorFocused ?? input.borderColorFocused,
    clearStyle: config?.clearStyle,
    clearButtonStyle: config?.clearButtonStyle,
    clearButtonIconSize: config?.clearButtonIconSize,
    clearButtonIconColor: config?.clearButtonIconColor,
    textColor: config?.textColor ?? input.textColor,
    textColorError: config?.textColorError ?? input.textColorError,
    textColorFocused: config?.textColorFocused ?? input.textColorFocused,
    placeholderTextColor:
      config?.placeholderTextColor ?? input.placeholderColor,
    withNativeClearButton: config?.withNativeClearButton ?? false,
  };
};

type ClearButtonConfig = {
  clearStyle?: StyleProp<ViewStyle>;
  clearButtonStyle?: StyleProp<ViewStyle>;
  clearButtonIconSize?: number;
  clearButtonIconColor?: string;
  withNativeClearButton?: boolean;
};

export type InputConfig = ClearButtonConfig & {
  padding?: number;
  fontSize?: number;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  borderColorError?: string;
  borderColorFocused?: string;
  contentHeight?: number;
  textColor?: string;
  textColorError?: string;
  textColorFocused?: string;
  placeholderTextColor?: string;
  containerStyle?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
};

export const InputContext = createContext<InputConfig | undefined>(undefined);

// represents the height for single line text input
const InputContainer = memo(
  ({
    focused,
    loading,
    showClearButton,
    errorMsg,
    errorBottomOffset,
    showError,
    multiline,
    style,
    clearStyle,
    clearButtonStyle,
    clearButtonIconSize,
    clearButtonIconColor,
    borderWidth,
    children,
    tag,
    onClear,
    voiceButton,
  }: {
    focused: boolean;
    showError: boolean;
    loading?: boolean;
    showClearButton?: boolean;
    errorMsg?: ErrorMsg;
    errorBottomOffset: number;
    multiline?: boolean;
    borderWidth: number;
    tag?: string;
    voiceButton?: ReactNode;
    style?: StyleProp<ViewStyle>;
    clearStyle?: StyleProp<ViewStyle>;
    clearButtonStyle?: StyleProp<ViewStyle>;
    clearButtonIconSize?: number;
    clearButtonIconColor?: string;
    onClear?: () => void;
    children?: (props: {
      errorDisplayStyle: ErrorDisplayStyle | undefined;
      height: number;
    }) => ReactNode;
  }) => {
    const config = useContext(InputContext);
    const baseStyle = useBaseInputStyle();
    const { rpx } = useResponsiveContext();
    const errorMsgRef = useRef<View>(null);
    const count = useRef(0);

    const { borderColor, borderColorError, borderColorFocused } = baseStyle;

    const [errorDisplayStyle, setErrorDisplayStyle] =
      useState<ErrorDisplayStyle>();

    const [height, setHeight] = useState<number>();
    const [errMsgHeight, setErrMsgHeight] = useState<number>();

    useEffect(() => {
      if (height == null || errMsgHeight == null) return;

      if (!showError) {
        setErrorDisplayStyle(undefined);
        return;
      }

      if (multiline) {
        // always use the default display style for multiline text
        setErrorDisplayStyle('default');
      } else {
        if (
          errMsgHeight + errorBottomOffset - borderWidth >=
          height / 2 /* small margin */
        ) {
          setErrorDisplayStyle('tip');
        } else {
          setErrorDisplayStyle('default');
        }
      }
    }, [
      showError,
      multiline,
      height,
      errMsgHeight,
      borderWidth,
      errorBottomOffset,
      rpx,
    ]);

    const onLayout = useCallback(
      (e: LayoutChangeEvent) => {
        const h = e.nativeEvent.layout.height;
        if (h !== height) {
          setHeight(h);
        }
      },
      [height],
    );

    const onErrorMsgLayout = useCallback((e: LayoutChangeEvent) => {
      setErrMsgHeight(e.nativeEvent.layout.height);
    }, []);

    count.current++;

    if (tag) {
      debug(`[${tag}] input container render #` + count.current);
    }

    return (
      <View
        style={[
          {
            borderRadius: baseStyle.borderRadius,
            borderWidth: borderWidth,
            borderColor: showError
              ? borderColorError
              : focused
              ? borderColorFocused
              : borderColor,
          },
          config?.containerStyle,
          style,
        ]}
        onLayout={onLayout}
      >
        {children?.({ errorDisplayStyle, height: height ?? 0 })}
        {loading && (
          <View
            css={`
              position: absolute;
              right: 0;
              top: 0;
              bottom: 0;
              padding-right: ${rpx(10)}px;
              flex-direction: row;
              align-items: center;
              justify-content: flex-end;
            `}
          >
            <ActivityIndicator
              size="small"
              style={{
                transform: [{ scale: 0.8 }],
              }}
            />
          </View>
        )}
        {errorMsg ? (
          <View
            ref={errorMsgRef}
            css={`
              position: absolute;
              left: ${rpx(10)}px;
              right: ${rpx(10)}px;
              bottom: 0px;
              opacity: 0;
              background-color: red;
            `}
            onLayout={onErrorMsgLayout}
          >
            <ErrorText errorMsg={errorMsg}></ErrorText>
          </View>
        ) : null}
        <AnimatePresence>
          {showClearButton && (
            <Center
              style={[
                {
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: rpx(kDefaultClearWidth),
                },
                config?.clearStyle,
                clearStyle,
              ]}
            >
              <FormElementClear
                style={[
                  { padding: rpx(4) },
                  config?.clearButtonStyle,
                  clearButtonStyle,
                ]}
                iconSize={
                  config?.clearButtonIconSize ??
                  clearButtonIconSize ??
                  rpx(kDefaultClearButtonSize)
                }
                iconColor={
                  config?.clearButtonIconColor ?? clearButtonIconColor ?? '#fff'
                }
                onPress={onClear}
              />
            </Center>
          )}
        </AnimatePresence>
        {voiceButton}
      </View>
    );
  },
);

const cssValue = (x: DimensionValue | undefined, d = 0): number => {
  if (x == null) return d;
  if (typeof x === 'string') return parseInt(x) || d;
  if (typeof x === 'number') return x;
  return d;
};

export type InputProps = TextInputProps &
  ClearButtonConfig & {
    errorMsg?: ErrorMsg;
    loading?: boolean;
    inputRef?: RefObject<TextInput>;
    tag?: string;
    interacted?: boolean;
    alwaysShowError?: boolean;
    containerStyle?:
      | StyleProp<ViewStyle>
      | ((focused: boolean) => StyleProp<ViewStyle>);
    bottomSheet?: boolean;
    errorBottomOffset?: number;
    withNativeClearButton?: boolean;
    /**
     * Enable voice-to-text input. Renders a microphone button on the right
     * side of the input that, when tapped, opens the global recording overlay
     * and appends the recognized text to the current value.
     */
    withVoiceInput?: boolean;
    /**
     * When `withVoiceInput` is enabled, by default the mic only shows while
     * the input is focused. Set this to `true` to keep it visible at all
     * times. Defaults to `false`.
     */
    alwaysShowVoiceInput?: boolean;
    /** Per-instance voice recognition tuning forwarded to the recognize API. */
    voiceInput?: VoiceRecognizeOptions;
    inputStyle?:
      | StyleProp<TextStyle>
      | ((focused: boolean) => StyleProp<TextStyle>);
  };

export const Input: FC<InputProps> = memo(
  ({
    style,
    inputStyle: userInputStyleOrFn,
    errorMsg,
    loading,
    interacted: _interacted,
    inputRef,
    tag,
    alwaysShowError,
    containerStyle: containerStyleOrFn,
    bottomSheet,
    errorBottomOffset,
    clearButtonMode = 'while-editing',
    clearStyle,
    clearButtonStyle,
    clearButtonIconSize,
    clearButtonIconColor,
    pointerEvents,
    withVoiceInput,
    alwaysShowVoiceInput,
    voiceInput,
    ...props
  }) => {
    const baseStyle = useBaseInputStyle();
    const { rpx } = useResponsiveContext();
    const [focused, setFocused] = useState(false);
    const interacted = useRef(_interacted ?? false);
    const changeInteracted = () => (interacted.current = true);

    // Internal fallback ref so we can refocus the input after recognition
    // completes regardless of whether the consumer passed `inputRef` in.
    const internalInputRef = useRef<TextInput>(null);
    const effectiveInputRef = inputRef ?? internalInputRef;

    // Track the last-known caret/selection so we can insert recognized text
    // at the cursor instead of always appending. `pendingSelection` is set
    // briefly after a programmatic insertion to move the caret to the end of
    // the inserted text; it's cleared immediately so the user regains free
    // control over the caret afterwards.
    const selectionRef = useRef<{ start: number; end: number }>({
      start: 0,
      end: 0,
    });
    const [pendingSelection, setPendingSelection] = useState<
      { start: number; end: number } | undefined
    >(undefined);
    useEffect(() => {
      if (pendingSelection) setPendingSelection(undefined);
    }, [pendingSelection]);
    const userOnSelectionChange = props.onSelectionChange;
    const onSelectionChange = useCallback(
      (e: Parameters<NonNullable<typeof userOnSelectionChange>>[0]) => {
        selectionRef.current = e.nativeEvent.selection;
        userOnSelectionChange?.(e);
      },
      [userOnSelectionChange],
    );

    // VoiceButton (rendered only when `withVoiceInput`) reports its
    // visible/active state up so we can reserve text padding and shift the
    // clear button out of its way. Inputs that don't opt into voice never
    // pay the cost of `useVoiceRecognize` since VoiceButton isn't mounted.
    const [voiceActive, setVoiceActive] = useState(false);
    const voiceButtonSize = rpx(28);

    if (!interacted.current && _interacted) {
      interacted.current = _interacted;
    }

    const containerStyle = useMemo(
      () =>
        typeof containerStyleOrFn === 'function'
          ? containerStyleOrFn(focused)
          : containerStyleOrFn,
      [focused, containerStyleOrFn],
    );

    const userInputStyle = useMemo(
      () =>
        typeof userInputStyleOrFn === 'function'
          ? userInputStyleOrFn(focused)
          : userInputStyleOrFn,
      [focused, userInputStyleOrFn],
    );

    const showError = Boolean(
      ((interacted.current && !focused) ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        (!focused && alwaysShowError) ||
        errorMsg instanceof ValidateErrorObject) &&
        errorMsg,
    );
    if (errorMsg && errorMsg instanceof ValidateErrorObject) {
      errorMsg = errorMsg.error;
    }

    const flattenedClearStyle = StyleSheet.flatten([
      baseStyle.clearStyle,
      clearStyle,
    ]);
    const clearWidth =
      cssValue(flattenedClearStyle.width) || rpx(kDefaultClearWidth);

    const flattenedStyle = StyleSheet.flatten(style) ?? ({} as ViewStyle);
    const padding = {
      left: cssValue(flattenedStyle.paddingLeft, baseStyle.padding),
      right: cssValue(flattenedStyle.paddingRight, baseStyle.padding),
      top: cssValue(flattenedStyle.paddingTop, baseStyle.padding),
      bottom: cssValue(flattenedStyle.paddingBottom, baseStyle.padding),
    };
    const borderWidth = cssValue(
      flattenedStyle.borderWidth ??
        flattenedStyle.borderTopWidth ??
        flattenedStyle.borderBottomWidth,
      baseStyle.borderWidth,
    );

    const withNativeClearButton =
      props.withNativeClearButton ?? baseStyle.withNativeClearButton;

    const clearButtonModeActive =
      clearButtonMode == 'always' ||
      (clearButtonMode == 'while-editing' && focused) ||
      (clearButtonMode == 'unless-editing' && !focused);

    const showClearButton = Boolean(
      props.value &&
        (Platform.OS !== 'ios' || !withNativeClearButton) &&
        clearButtonModeActive,
    );

    // True when the iOS-native clear-X is going to render at the right edge.
    // We can't reposition it, so the voice mic must shift inward to clear it.
    const showNativeClearButton = Boolean(
      props.value &&
        Platform.OS === 'ios' &&
        withNativeClearButton &&
        clearButtonModeActive,
    );

    // Voice mic right offset: sit at the edge normally, but when the native
    // clear is taking up the right edge, push the mic inward so it doesn't
    // overlap. iOS's UITextField clear-X reserves ~25pt at the right edge,
    // so we inset by rpx(32) for a comfortable gap.
    const voiceRightOffset = showNativeClearButton ? rpx(32) : rpx(8);
    const voicePadding = voiceActive
      ? voiceButtonSize + voiceRightOffset + rpx(8)
      : 0;

    const onClear = useCallback(() => {
      props.onChangeText?.('');
    }, [props]);

    return (
      <InputContainer
        borderWidth={borderWidth}
        focused={focused}
        loading={loading}
        errorMsg={errorMsg}
        errorBottomOffset={errorBottomOffset ?? baseStyle.errorBottomOffset}
        showError={showError}
        tag={tag}
        style={containerStyle}
        multiline={props.multiline}
        showClearButton={showClearButton}
        // When voice mic is showing alongside the JS clear button, shift the
        // JS clear left so they don't overlap. (Native clear case is handled
        // by shifting the mic inward instead — see voiceRightOffset.)
        clearStyle={
          voiceActive
            ? [
                { right: voiceButtonSize + voiceRightOffset + rpx(8) },
                clearStyle,
              ]
            : clearStyle
        }
        clearButtonStyle={clearButtonStyle}
        clearButtonIconSize={clearButtonIconSize}
        clearButtonIconColor={clearButtonIconColor}
        onClear={onClear}
        voiceButton={
          withVoiceInput ? (
            <VoiceButton
              visible={Boolean(alwaysShowVoiceInput || focused)}
              rightOffset={voiceRightOffset}
              multiline={props.multiline}
              voiceInput={voiceInput}
              inputRef={effectiveInputRef}
              value={props.value ?? ''}
              onChangeText={props.onChangeText}
              selectionRef={selectionRef}
              setPendingSelection={setPendingSelection}
              onActiveChange={setVoiceActive}
            />
          ) : undefined
        }
      >
        {({ errorDisplayStyle, height }) => (
          <>
            {bottomSheet ? (
              <MotiBottomSheetTextInput
                ref={effectiveInputRef}
                scrollEnabled={false}
                pointerEvents={pointerEvents}
                transition={{ type: 'timing', duration: 200 }}
                animate={{
                  translateY:
                    errorDisplayStyle === 'default'
                      ? baseStyle.inputOffsetOnError
                      : 0,
                }}
                css={`
                  padding: ${baseStyle.padding}px;
                  padding-right: ${baseStyle.padding +
                  (showClearButton ? clearWidth : 0) +
                  voicePadding}px;
                  font-size: ${baseStyle.fontSize}px;
                  color: ${showError
                    ? baseStyle.textColorError
                    : focused
                    ? baseStyle.textColorFocused
                    : baseStyle.textColor};
                  font-family: ${showError
                    ? FontFamily.NotoSans.Bold
                    : props.value
                    ? FontFamily.NotoSans.Medium
                    : FontFamily.NotoSans.Light};
                `}
                placeholderTextColor={baseStyle.placeholderTextColor}
                clearButtonMode={
                  Platform.OS === 'ios' && withNativeClearButton
                    ? clearButtonMode
                    : 'never'
                }
                {...props}
                onFocus={juxt([
                  props.onFocus ?? identity,
                  partial(setFocused, [true]),
                  changeInteracted,
                ])}
                onBlur={juxt([
                  props.onBlur ?? identity,
                  partial(setFocused, [false]),
                ])}
                onSelectionChange={onSelectionChange}
                selection={pendingSelection ?? props.selection}
                style={[style, userInputStyle, { borderWidth: 0 }]}
              />
            ) : (
              <MotiTextInput
                ref={effectiveInputRef}
                scrollEnabled={false}
                pointerEvents={pointerEvents}
                transition={{ type: 'timing', duration: 200 }}
                animate={{
                  translateY:
                    errorDisplayStyle === 'default'
                      ? baseStyle.inputOffsetOnError
                      : 0,
                }}
                css={`
                  padding: ${baseStyle.padding}px;
                  padding-right: ${baseStyle.padding +
                  (showClearButton ? clearWidth : 0) +
                  voicePadding}px;
                  font-size: ${baseStyle.fontSize}px;
                  color: ${showError
                    ? baseStyle.textColorError
                    : focused
                    ? baseStyle.textColorFocused
                    : baseStyle.textColor};
                  font-family: ${showError
                    ? FontFamily.NotoSans.Medium
                    : props.value
                    ? FontFamily.NotoSans.Medium
                    : FontFamily.NotoSans.Light};
                `}
                placeholderTextColor={baseStyle.placeholderTextColor}
                clearButtonMode={
                  Platform.OS === 'ios' && withNativeClearButton
                    ? clearButtonMode
                    : 'never'
                }
                {...props}
                onFocus={juxt([
                  props.onFocus ?? identity,
                  partial(setFocused, [true]),
                  changeInteracted,
                ])}
                onBlur={juxt([
                  props.onBlur ?? identity,
                  partial(setFocused, [false]),
                ])}
                onSelectionChange={onSelectionChange}
                selection={pendingSelection ?? props.selection}
                style={[style, userInputStyle, { borderWidth: 0 }]}
              />
            )}
            <InputError
              left={padding.left}
              right={padding.right}
              containerHeight={height - padding.top - padding.bottom - rpx(4)}
              errorDisplayStyle={errorDisplayStyle}
              errorBottomOffset={
                errorBottomOffset ?? baseStyle.errorBottomOffset
              }
              errorMsg={errorMsg}
            />
          </>
        )}
      </InputContainer>
    );
  },
);

const InputError = memo(
  ({
    containerHeight,
    left,
    right,
    errorMsg,
    errorDisplayStyle,
    errorBottomOffset,
  }: {
    containerHeight: number;
    left: number;
    right: number;
    errorDisplayStyle?: ErrorDisplayStyle;
    errorMsg?: ErrorMsg;
    errorBottomOffset: number;
  }) => {
    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <AnimatePresence>
        {errorMsg != null && errorDisplayStyle != null && (
          <MotiView
            from={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            css={`
              position: absolute;
              left: ${errorDisplayStyle === 'default' ? left : 0}px;
              right: ${errorDisplayStyle === 'default' ? right : 0}px;
              bottom: ${errorDisplayStyle === 'default'
                ? errorBottomOffset
                : containerHeight}px;
              background-color: transparent;
            `}
          >
            {errorDisplayStyle === 'default' ? (
              <ErrorText errorMsg={errorMsg}></ErrorText>
            ) : (
              <ErrorTip errorMsg={errorMsg} />
            )}
          </MotiView>
        )}
      </AnimatePresence>
    );
  },
);

export const ErrorText = memo(
  ({
    errorMsg,
    style,
  }: {
    errorMsg?: ErrorMsg;
    style?: StyleProp<TextStyle>;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    return (
      <MaybeText
        text={errorMsg as any}
        numberOfLines={0}
        css={`
          color: ${theme.form.input.borderColorError};
          font-size: ${rpx(11)}px;
          line-height: ${rpx(14)}px;
          font-family: ${FontFamily.NotoSans.Regular};
        `}
        style={style}
      />
    );
  },
);

const ErrorTip = memo(
  (props: { errorMsg: ErrorMsg; style?: StyleProp<ViewStyle> }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    return (
      <View>
        <View
          css={`
            background-color: ${theme.form.input.borderColorError};
            border-radius: ${rpx(4)}px;
            padding: ${rpx(5)}px ${rpx(10)}px;
          `}
          style={props.style}
        >
          <ErrorText
            errorMsg={props.errorMsg}
            css={`
              color: #fff;
              line-height: ${rpx(16)}px;
            `}
          />
        </View>
        <View
          css={`
            background-color: transparent;
            width: 0;
            height: 0;
            margin-left: ${rpx(20)}px;
            border-width: ${rpx(5)}px;
            border-color: transparent;
            border-top-color: ${theme.form.input.borderColorError};
          `}
        />
      </View>
    );
  },
);

const VoiceButton = memo(
  ({
    visible,
    multiline,
    rightOffset,
    voiceInput,
    inputRef,
    value,
    onChangeText,
    selectionRef,
    setPendingSelection,
    onActiveChange,
  }: {
    visible: boolean;
    multiline?: boolean;
    /** Distance from the right edge in pixels. */
    rightOffset: number;
    voiceInput?: VoiceRecognizeOptions;
    inputRef: RefObject<TextInput>;
    value: string;
    selectionRef: RefObject<{ start: number; end: number }>;
    setPendingSelection: (sel: { start: number; end: number }) => void;
    onActiveChange?: (active: boolean) => void;
    onChangeText?: (text: string) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const voice = useVoiceRecognize(voiceInput);

    const showMic = visible && !voice.isRecording && !voice.isRecognizing;
    const showSpinner = voice.isRecognizing;
    const active = showMic || showSpinner;

    useEffect(() => {
      onActiveChange?.(active);
    }, [active, onActiveChange]);

    const onMicPress = useCallback(() => {
      voice.start({
        onResult: recognized => {
          // Refocus so the keyboard slides back up and (in the focus-gated
          // mode) the mic remains visible for follow-up dictation.
          inputRef.current?.focus();
          const sel = selectionRef.current ?? { start: 0, end: 0 };
          const start = Math.min(Math.max(sel.start, 0), value.length);
          const end = Math.min(Math.max(sel.end, start), value.length);
          const next = value.slice(0, start) + recognized + value.slice(end);
          const caret = start + recognized.length;
          onChangeText?.(next);
          setPendingSelection({ start: caret, end: caret });
          (
            selectionRef as { current: { start: number; end: number } }
          ).current = { start: caret, end: caret };
        },
      });
    }, [
      voice,
      inputRef,
      value,
      onChangeText,
      selectionRef,
      setPendingSelection,
    ]);

    if (!showMic && !showSpinner) return null;

    const buttonSize = rpx(28);
    const iconSize = rpx(14);
    const pillStyle = {
      width: buttonSize,
      height: buttonSize,
      borderRadius: buttonSize / 2,
      backgroundColor: theme.link,
      shadowColor: '#000',
      shadowOpacity: 0.18,
      shadowRadius: rpx(4),
      shadowOffset: { width: 0, height: rpx(2) },
      elevation: 3,
    } as const;

    return (
      <Center
        style={[
          { position: 'absolute', right: rightOffset, width: buttonSize },
          multiline
            ? { bottom: rpx(8), height: buttonSize }
            : { top: 0, bottom: 0 },
        ]}
      >
        {showSpinner ? (
          <Center style={pillStyle}>
            <Spinner size={iconSize} color="#fff" />
          </Center>
        ) : (
          <Center.Touchable
            activeOpacity={0.7}
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
            onPress={onMicPress}
            style={pillStyle}
          >
            <Mic color="#fff" size={iconSize} strokeWidth={2.4} />
          </Center.Touchable>
        )}
      </Center>
    );
  },
);
